/**
 * DESIGN.md §7.3 — Pill Buttons.
 *
 * Three sizes × two color schemes plus an outline variant. Size and
 * scheme are INDEPENDENT (per §7.3 explicit guidance: "either size
 * may take either scheme"). The outline variant forces its own scheme
 * because it's a different anatomy (border, no fill).
 *
 * Matrix:
 *   - `md`  · primary (jet)   →  padding 24×12 · semibold (600)
 *   - `md`  · accent (flame)  →  padding 24×12 · semibold
 *   - `lg`  · primary         →  padding 24×16 · medium (500)
 *   - `lg`  · accent          →  padding 24×16 · medium
 *   - `lg-outline` · outline  →  padding 24×16 · medium · border, no fill
 *
 * Text color is text/on-action-1 (white) on every filled scheme;
 * outline uses text/on-action-2 (jet/500).
 */
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { externalLinkProps } from '../lib/external-link.js';

const BASE =
  'inline-flex items-center justify-center font-label rounded-xl transition-colors whitespace-nowrap select-none';

export const pillButtonVariants = cva(BASE, {
  variants: {
    size: {
      md: 'px-6 py-3 text-label-md font-semibold',
      lg: 'px-6 py-4 text-label-md font-medium',
      'lg-outline': 'px-6 py-4 text-label-md font-medium border',
    },
    scheme: {
      primary:
        'bg-surface-action-1 hover:bg-surface-action-hover-1 active:bg-surface-action-click-1 text-text-on-action-1',
      accent:
        'bg-surface-action-2 hover:bg-surface-action-hover-2 active:bg-surface-action-click-2 text-text-on-action-1',
      outline:
        'border-border-action text-text-on-action-2 hover:border-border-hover hover:text-text-hover-1 active:border-border-click active:text-text-click-1',
    },
  },
  compoundVariants: [
    // Outline forces outline scheme; ignore + override on misuse.
    { size: 'lg-outline', scheme: 'primary', className: '' },
    { size: 'lg-outline', scheme: 'accent', className: '' },
  ],
  defaultVariants: { size: 'lg', scheme: 'primary' },
});

export interface PillButtonProps
  extends Omit<ComponentProps<'button'>, 'children'>,
    VariantProps<typeof pillButtonVariants> {
  /** Render as an anchor when href is supplied. */
  href?: string;
  children: ReactNode;
  /** Stretch to full container width — used by the §7.6 Subscription Card CTA. */
  fullWidth?: boolean;
}

export function PillButton({ size, scheme, href, className, children, fullWidth, ...props }: PillButtonProps) {
  // §7.3: outline variant forces outline scheme regardless of caller intent.
  const resolvedScheme = size === 'lg-outline' ? 'outline' : (scheme ?? 'primary');
  const klass = cn(pillButtonVariants({ size, scheme: resolvedScheme }), fullWidth && 'w-full', className);

  if (href) {
    return (
      <a href={href} {...externalLinkProps(href)} className={klass}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={klass} {...props}>
      {children}
    </button>
  );
}
